import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getDefaultDateRange } from '../utils/usage-insights.utils';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readIsoDate(params: URLSearchParams, key: string, fallback: string): string {
  const value = params.get(key)?.trim() ?? '';
  return ISO_DATE.test(value) ? value : fallback;
}

function readPage(params: URLSearchParams): number {
  const raw = params.get('page');
  const parsed = raw ? Number.parseInt(raw, 10) : 1;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function useUsageInsightsUrlParams() {
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [from, setFromState] = useState(() => readIsoDate(searchParams, 'from', defaults.from));
  const [to, setToState] = useState(() => readIsoDate(searchParams, 'to', defaults.to));
  const [workspaceId, setWorkspaceIdState] = useState(() => searchParams.get('workspace_id') || '');
  const [page, setPageState] = useState(() => readPage(searchParams));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFromState(readIsoDate(params, 'from', defaults.from));
    setToState(readIsoDate(params, 'to', defaults.to));
    setWorkspaceIdState(params.get('workspace_id') || '');
    setPageState(readPage(params));
  }, [defaults.from, defaults.to, location.search]);

  const updateParams = useCallback((
    updater: (params: URLSearchParams) => void,
    options?: { resetPage?: boolean },
  ) => {
    const nextParams = new URLSearchParams(searchParams);
    updater(nextParams);
    if (options?.resetPage) {
      nextParams.delete('page');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const setFrom = useCallback((value: string) => {
    setFromState(value);
    updateParams((params) => {
      if (value) {
        params.set('from', value);
      } else {
        params.delete('from');
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setTo = useCallback((value: string) => {
    setToState(value);
    updateParams((params) => {
      if (value) {
        params.set('to', value);
      } else {
        params.delete('to');
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setWorkspaceId = useCallback((value: string) => {
    setWorkspaceIdState(value);
    updateParams((params) => {
      if (value) {
        params.set('workspace_id', value);
      } else {
        params.delete('workspace_id');
      }
    }, { resetPage: true });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    setPageState(value);
    updateParams((params) => {
      if (value <= 1) {
        params.delete('page');
      } else {
        params.set('page', String(value));
      }
    });
  }, [updateParams]);

  return {
    from,
    to,
    workspaceId,
    page,
    setFrom,
    setTo,
    setWorkspaceId,
    setPage,
  };
}
